import React, {
  memo,
  useMemo,
  useRef,
  useCallback,
  useEffect,
  useState,
} from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useVideoState, VideoEvents } from '@msi-alta/opvideo-state-machine';

import { selectCurrentUserId } from 'global/accessToken/selectors';
import { useMicrophonePermission, useSelectorJs } from 'utils/customHooks';
import { opMoment } from 'utils/dates';
import { getOpParams } from 'utils/getOpParams';
import { selectCurrentIdentityLanguage } from 'routes/AppContainer/selectors';
import { setAlert } from 'routes/AppContainer/actions';
import LiveKitVideoPlayer from 'new-components/LiveKitVideoPlayer/LiveKitVideoPlayer';
import { VideoPlayer as VideoPlayerComponent } from 'containers/OpVideoPlayer/components';
import { useOpVideoPlayerData } from 'containers/OpVideoPlayer/helpers/useOpVideoPlayerData';
import { useHls } from 'containers/OpVideoPlayer/helpers/useHls';
import { useIntercomCallButtonClick } from 'containers/OpVideoPlayer/helpers/useIntercomCallButtonClick';
import { useUpperControlsContent } from 'containers/OpVideoPlayer/helpers/useUpperControlsContent';
import { useVideoLoadingEvents } from 'containers/OpVideoPlayer/helpers/useVideoLoadingEvents';

interface VideoPlayerProps {
  ref?: React.Ref<any>;
  isLoading?: boolean;
  width?: string | number;
  height?: string | number;
  url?: string;
  aspectRatio?: number;
  muted?: boolean;
  preventedHotkeys?: string[];
  preventVideoClick?: boolean;
  isLive?: boolean;
  controls?: boolean;
  upperControls?: React.ReactElement;
  errorProps?: {
    buttonContent?: string;
    errorMessage?: any;
    handleRetryClick?: () => void;
    iconName?: string;
  };
  hiddenElements?: string[];
  upperControlsContent?: {
    left?: {
      fadeIn?: boolean;
      content?: any;
    };
    center?: {
      fadeIn?: boolean;
      content?: any;
    };
    right?: {
      fadeIn?: boolean;
      content?: any;
    };
  };
  videoDownloadButtonProps?: {
    opvideoDeviceId: number;
    startDate: string;
    endDate: string;
  };
  initialVolume?: number;
  [key: string]: any;
}

const VideoPlayer = VideoPlayerComponent as React.FC<VideoPlayerProps>;

// TODO: Update opvideo-state-machine so that states can be imported when isolatedModules is enabled
const VideoStates = {
  CONNECTING: 'CONNECTING',
  CONNECTED: 'CONNECTED',
  PREVIEW: 'PREVIEW',
  SURVEILLANCE: 'SURVEILLANCE',
  CALL: 'CALL',
  FAILED: 'FAILED',
  RECONNECTING: 'RECONNECTING',
  DISCONNECTING: 'DISCONNECTING',
  DISCONNECTED: 'DISCONNECTED',
};

interface OpVideoPlayerProps {
  opvideoDeviceId: number;
  autoPlay?: boolean;
  controls?: boolean;
  isLive?: boolean;
  isIncomingCall?: boolean;
  startAt?: string;
  endAt?: string;
  unixTimestamp?: number;
  isClip?: boolean;
  livekitAuth?: any;
  livekitRoom?: any;
  [key: string]: any;
}

const NewOpVideoPlayer: React.FC<OpVideoPlayerProps> = ({
  opvideoDeviceId,
  autoPlay = true,
  controls = true,
  isLive = false,
  isIncomingCall = false,
  startAt = '', // Date string (e.g. 2022-03-30T18:26:23.000Z)
  endAt = '', // Date string (e.g. 2022-03-30T18:26:34.968Z)
  unixTimestamp = 0, // Timestamp in seconds
  isClip = false,
  livekitAuth = null,
  livekitRoom = null,
  // Passed through to <video>
  ...videoPlayerProps
}) => {
  const { t } = useTranslation();

  /*
    LiveKit:
    Both preview and call run under the same LiveKit call.
    When you hit the "call" button, the mic is enabled
  */
  const [errorProps, setErrorProps] = useState<any>(null);
  const [initialVolume, setInitialVolume] = useState<number>(isLive ? 0 : 1);
  const [shouldStopVideoCall, setShouldStopVideoCall] =
    useState<boolean>(false);
  const [hasShownMicAlert, sethasShownMicAlert] = useState<boolean>(false);

  const playerRef = useRef<any>(null);
  const dispatch = useDispatch();
  const micPermission = useMicrophonePermission();
  const currentUserId = useSelectorJs(selectCurrentUserId());
  const currentIdentityLanguage = useSelectorJs(
    selectCurrentIdentityLanguage(),
  );

  const { videoState, sendEvent } = useVideoState();
  const { value: currentVideoState, context: videoStateContext } = videoState;
  const { orgId } = getOpParams();

  const handleMicState = useCallback(() => {
    sendEvent({
      type: videoStateContext.micEnabled
        ? VideoEvents.MicDisabled
        : VideoEvents.MicEnabled,
    });
  }, [sendEvent, videoStateContext.micEnabled]);

  const isMicEnabled = useCallback(() => {
    return videoStateContext.micEnabled;
  }, [videoStateContext.micEnabled]);

  const micPermissionStatus = useMicrophonePermission();

  // @ws TODO: drop opvideoDeviceStreamingSubscriberToken scope checks littered throughout codebase and make them camera-specific not opvideoDevice-specific 🙃

  const { playerValues, isLoading, setIsLoading } = useOpVideoPlayerData({
    orgId,
    opvideoDeviceId,
    currentIdentityLanguage,
    isLive,
    startAt,
    endAt,
    unixTimestamp,
    sendEvent,
    VideoEvents,
    isIncomingCall,
    setErrorProps,
    t,
  });

  const { opvideoDevice, cameraStaleSnapshot } = (playerValues as any) || {};
  /*
    Destructuring here to prevent issues when items are null
    (as default prop only works when value is undefined)
  */
  const {
    cameraId,
    opvideoIntercom,
    recordPreEventSeconds,
    recordPostEventSeconds,
  } = opvideoDevice || {};

  const { id: opvideoIntercomId } = opvideoIntercom || {};
  const { blurredThumbnailUrl } = cameraStaleSnapshot || {};

  /*
    Create startAt and endAt values
    As this is only used for entry unlock events we are not taking clip length
    into consideration and simply using the pre/post recording buffer to create
    a consistent length clip
  */
  const startDate =
    startAt ||
    opMoment.unix(unixTimestamp - recordPreEventSeconds).toISOString();
  const endDate =
    endAt ||
    opMoment.unix(unixTimestamp + recordPostEventSeconds).toISOString();

  useHls({
    playerRef,
    autoPlay,
    startDate,
    endDate,
    errorProps,
    setErrorProps,
    setIsLoading,
    orgId,
    opvideoDeviceId,
    shouldRun:
      !isLive &&
      playerRef.current &&
      !errorProps &&
      recordPreEventSeconds &&
      recordPostEventSeconds,
  });

  // Destroy the player on unmount
  useEffect(
    () => () => {
      // Destroy playerRef
      if (playerRef.current) {
        playerRef.current = null;
      }
    },
    [],
  );

  useEffect(() => {
    sendEvent({
      type:
        micPermissionStatus === 'granted'
          ? VideoEvents.MicPermissionEnabled
          : VideoEvents.MicPermissionDisabled,
    });
  }, [micPermissionStatus, sendEvent]);

  const intercomCallButtonClick = useIntercomCallButtonClick({
    currentVideoState,
    sendEvent,
    handleMicState,
  });

  // Manage video volume
  useEffect(() => {
    if (
      currentVideoState === VideoStates.PREVIEW ||
      currentVideoState === VideoStates.SURVEILLANCE
    ) {
      setInitialVolume(0);
    } else if (currentVideoState === VideoStates.CALL) {
      setInitialVolume(1);
    }
  }, [currentVideoState]);

  useEffect(() => {
    if (
      !isClip &&
      (currentVideoState === VideoStates.PREVIEW ||
        currentVideoState === VideoStates.SURVEILLANCE) &&
      micPermission === 'denied' &&
      !hasShownMicAlert
    ) {
      dispatch(setAlert('warning', t(`Browser microphone permission denied.`)));
      sethasShownMicAlert(true);
    }
  }, [dispatch, hasShownMicAlert, isClip, micPermission, t, currentVideoState]);

  const upperControlsContent = useUpperControlsContent({
    controls,
    opvideoIntercomId,
    currentVideoState,
    isClip,
    intercomCallButtonClick,
    micPermission,
    isLive,
    cameraId,
    handleMicState,
    isMicEnabled,
    startDate,
    endDate,
    orgId,
    opvideoDeviceId,
  });

  const preventedHotkeys = useMemo(
    () => (isLive ? [' ', 'ArrowLeft', 'ArrowRight'] : []),
    [isLive],
  );

  const hiddenElements = useMemo(() => {
    const elementsToHide = [];

    // Hide items when live streaming
    if (isLive) {
      elementsToHide.push(
        'BigPlayButton',
        'PlayButton',
        'SeekBar',
        'TimeDisplay',
      );
    } else {
      elementsToHide.push('LiveButton');
    }

    // Hide volume button if not an intercom or if the mic is not enabled
    if (
      !opvideoIntercomId ||
      !opvideoDevice?.opvideoIntercom?.isMicrophoneEnabled
    ) {
      elementsToHide.push('VolumeButton');
    }

    return elementsToHide;
  }, [
    isLive,
    opvideoIntercomId,
    opvideoDevice?.opvideoIntercom?.isMicrophoneEnabled,
  ]);

  const videoPlayerErrorProps = useMemo(
    () => ({
      handleRetryClick: () => {
        setErrorProps(null);
        setIsLoading(true);
      },
      ...(errorProps && typeof errorProps === 'object' ? errorProps : {}),
    }),
    [errorProps, setIsLoading],
  );

  // Stop loader if there is an error
  useEffect(() => {
    if (errorProps) {
      setIsLoading(false);
    }
  }, [errorProps, setIsLoading]);

  // Updates on callState changes
  useEffect(() => {
    if (
      shouldStopVideoCall &&
      currentVideoState === VideoStates.DISCONNECTED &&
      opvideoIntercomId
    ) {
      setShouldStopVideoCall(false);
      setInitialVolume(0);
    }
    // Clear any errors so we can view intercom call unobstructed
    if (currentVideoState === VideoStates.CONNECTED) {
      setErrorProps(null);
      sendEvent({ type: VideoEvents.VideoDetails });
    }
  }, [
    currentIdentityLanguage,
    currentUserId,
    currentVideoState,
    opvideoIntercomId,
    orgId,
    sendEvent,
    shouldStopVideoCall,
  ]);

  useVideoLoadingEvents({
    playerRef,
    setIsLoading,
  });

  return (
    <div className="op-line-height-normal" data-testid="newopvideoplayer">
      {' '}
      {/** Prevents alignment issues caused by .ui.dimmer */}
      <VideoPlayer
        ref={playerRef}
        isLoading={isLoading}
        muted // Must mute when possible so that autoplay is most consistent
        playsInline
        preventVideoClick
        poster={blurredThumbnailUrl}
        {...{
          autoPlay,
          controls,
          upperControlsContent,
          preventedHotkeys,
          hiddenElements,
          initialVolume,
          ...(errorProps && typeof errorProps === 'object'
            ? { errorProps: videoPlayerErrorProps }
            : {}),
          // Everything else gets passed through
          ...videoPlayerProps,
        }}
      />
      {!isClip && (
        <LiveKitVideoPlayer
          ref={playerRef}
          {...{
            room: livekitRoom,
            token: livekitAuth?.token,
            wsUrl: livekitAuth?.wsUrl,
            isMicrophoneEnabled: isMicEnabled(),
            setErrorProps,
            setIsLoading,
            orgId,
            opvideoDeviceId,
            currentUserId,
            currentIdentityLanguage,
          }}
        />
      )}
    </div>
  );
};

export default memo(NewOpVideoPlayer);
